import Raven from 'raven-js';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { CertificateImage as PrivateCertificateImage } from '../../../features/achievementImages';
import { addUrlParams } from 'LearningCenterUI/utils/RoutingUtils';
import { useAchievementImageUrl, useAchievementTrackId, usePublicAchievement } from '../achievementQueries';
import ImageLoader from '../../../components/ImageLoader';
import { View } from 'foundations-components/components/View';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Small } from 'foundations-components/transitional/components/Small';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Pictogram } from 'foundations-components/transitional/components/Pictogram';
import { getMarketingThemePreference } from 'academy-common-lib/features/publicTheme/themeUtils';
import { isPrerenderAgent } from 'academy-common-lib/utils/url';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MarketingContainer = ({
  children,
  isBadge
}) => /*#__PURE__*/_jsx(View, {
  backgroundColor: "fill-surface-default",
  borderRadius: "300",
  boxShadow: "400",
  "data-test-id": "achievement-cert-image",
  marginBlockStart: "-1000",
  overflow: "hidden",
  children: isBadge ? /*#__PURE__*/_jsx(Flex, {
    alignItems: "center",
    justifyContent: "center",
    padding: "1000",
    children: children
  }) : children
});
const LegacyContainer = ({
  children,
  isBadge
}) => {
  const {
    isTablet
  } = useMediaQuery();
  return /*#__PURE__*/_jsx(View, {
    backgroundColor: "fill-surface-default",
    borderRadius: "300",
    boxShadow: "400",
    "data-test-id": "achievement-cert-image",
    marginBlockEnd: isTablet ? '0' : '-700',
    marginBlockStart: "-700",
    overflow: "hidden",
    children: isBadge ? /*#__PURE__*/_jsx(Flex, {
      alignItems: "center",
      justifyContent: "center",
      padding: "1000",
      children: children
    }) : children
  });
};
const handleImageError = ({
  currentTarget
}) => {
  if (isPrerenderAgent()) return;
  Raven.captureMessage(`Achievements: error loading cert image`, {
    extra: {
      src: currentTarget.src
    }
  });
};
const CertificateImage = ({
  inModal,
  publicId
}) => {
  const {
    publicAchievement
  } = usePublicAchievement(publicId);
  const trackId = useAchievementTrackId(publicId);
  const imageUrl = useAchievementImageUrl(publicId);
  const src = imageUrl !== null && imageUrl !== void 0 ? imageUrl : '';
  const isBadgeOnly = (publicAchievement === null || publicAchievement === void 0 ? void 0 : publicAchievement.credentialType) === 'BADGE';
  const aspectRatio = isBadgeOnly ? '1/1' : '4/3';
  const maybeTrackedSrc = !inModal && src && !src.match(/^data:/) ? addUrlParams(src, {
    onAchievementsPage: true
  }) : src;
  if (!publicAchievement || !maybeTrackedSrc && !trackId) {
    return /*#__PURE__*/_jsx("div", {
      "data-test-id": "cert-image-wrapper",
      style: {
        aspectRatio
      },
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        blockSize: "100%",
        flexDirection: "column",
        justifyContent: "center",
        padding: "1000",
        children: [/*#__PURE__*/_jsx(Pictogram, {
          name: "Error"
        }), /*#__PURE__*/_jsx(Text, {
          as: "p",
          marginBlockStart: "400",
          typography: "heading-200",
          children: I18n.text('learningCenter.certification.errors.noCertSrcTitle')
        }), /*#__PURE__*/_jsx(Text, {
          as: "p",
          marginBlockStart: "200",
          textAlign: "center",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.certification.errors.noCertSrc"
          })
        })]
      })
    });
  }
  const Container = getMarketingThemePreference() ? MarketingContainer : LegacyContainer;
  return /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    flexDirection: "column",
    children: [/*#__PURE__*/_jsx(Container, {
      isBadge: isBadgeOnly,
      children: /*#__PURE__*/_jsx("div", {
        "data-test-id": "cert-image-wrapper",
        style: {
          aspectRatio,
          maxInlineSize: isBadgeOnly ? 320 : undefined
        },
        children: /*#__PURE__*/_jsx(ImageLoader, Object.assign({}, publicAchievement.visibility === 'PRIVATE' ? {
          trackId,
          component: PrivateCertificateImage,
          key: trackId
        } : {
          src: maybeTrackedSrc,
          key: maybeTrackedSrc
        }, {
          alt: I18n.text(isBadgeOnly ? 'learningCenter.certification.badgeAltText' : 'learningCenter.certification.altText'),
          errorComponent: /*#__PURE__*/_jsx(TFlex, {
            alignItems: "center",
            blockSize: "100%",
            justifyContent: "center",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.certification.errors.imgLoadingError"
            })
          }),
          onError: handleImageError
        }))
      })
    }), isBadgeOnly && /*#__PURE__*/_jsxs(View, {
      paddingBlockStart: "600",
      textAlign: "center",
      children: [publicAchievement.awardedAt && /*#__PURE__*/_jsx(Small, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.certification.issuedDate",
          options: {
            date: new Date(publicAchievement.awardedAt).toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            })
          }
        })
      }), /*#__PURE__*/_jsx("br", {}), publicAchievement.code && /*#__PURE__*/_jsx(Small, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.certification.certificationCode",
          options: {
            code: publicAchievement.code
          }
        })
      })]
    })]
  });
};
export default CertificateImage;